import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, Select, MenuItem, InputLabel, FormControl, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiRequest, apiGet } from '../../../utils/fetch';
import { AdSpaceInterface, GeoJsonPoint, SizeDataTypeInterface, 
  AdSpaceMediaTypeInterface, CurrencyInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialAdSpace: Partial<AdSpaceInterface> = {
  userId: 0,
  geoName: '',
  description: '',
  adSpaceMediaTypeId: 0,
  sizeWidth: 0,
  sizeHeight: 0,
  sizeDataTypeId: 0,
  country: '',
  province: '',
  city: '',
  area: '',
  street: '',
  longLatCoords: { type: 'Point', coordinates: [0, 0] },
  priceWeekly: 0,
  priceMonthly: 0,
  priceYearly: 0,
  currencyId: 1, // default currency id
  availableFrom: new Date(),
  active: true,
};

const AdSpacesAdminAddAdSpacePage: React.FC = () => {
    const [adSpace, setAdSpace] = useState<Partial<AdSpaceInterface>>(initialAdSpace);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [userId, setUserId] = useState<string>('');
    const [lat, setLat] = useState<string>(''); 
    const [lng, setLng] = useState<string>('');
    const [mediaTypes, setMediaTypes] = useState<AdSpaceMediaTypeInterface[]>([]);
    const [sizeDataTypes, setSizeDataTypes] = useState<SizeDataTypeInterface[]>([]);
    const [currencies, setCurrencies] = useState<CurrencyInterface[]>([]);    

  const handleChange = (field: keyof AdSpaceInterface, value: any) => {
    setAdSpace(a => ({ ...a, [field]: value }));
  };

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const latNum = lat === '' ? undefined : Number(lat);
    const lngNum = lng === '' ? undefined : Number(lng);

    // GeoJSON format: [longitude, latitude] (PostGIS/GeoJSON standard)
    // Google Maps shows: latitude, longitude
    // So we convert: lat, lng → [lng, lat]
    const longLatCoords: GeoJsonPoint | undefined =
        typeof latNum === 'number' && !isNaN(latNum) &&
        typeof lngNum === 'number' && !isNaN(lngNum)
            ? { type: "Point", coordinates: [lngNum, latNum] }
            : undefined;

    const userIdNum = userId === '' ? undefined : Number(userId);
    try {
        const result = await apiRequest<GenericResponseInterface<AdSpaceInterface>>('/adSpaces/insert', 'POST', {
            ...adSpace,
            longLatCoords,
            userId: userIdNum,
        });
        if (result.status) {
            setSuccess(result.message || 'Ad space created successfully!');
            setDialogOpen(true);
            setAdSpace(initialAdSpace);
            setLat('');
            setLng('');
        } else {
            setError(result.message || 'Failed to create ad space');
            setDialogOpen(true);
        }
    } catch (err: any) {
        setError(err.message || 'Failed to create ad space');
        setDialogOpen(true);
    }
};

  useEffect(() => {
    apiGet<GenericResponseInterface<AdSpaceMediaTypeInterface[]>>('/adSpaceMediaTypes').then(result => {
      if (result.status && result.data) {
        setMediaTypes(result.data);
      }
    });
    apiGet<GenericResponseInterface<SizeDataTypeInterface[]>>('/sizeDataTypes').then(result => {
      if (result.status && result.data) {
        setSizeDataTypes(result.data);
      }
    });
    apiGet<GenericResponseInterface<CurrencyInterface[]>>('/currencies').then(result => {
      if (result.status && result.data) {
        setCurrencies(result.data);
      }
    });
  }, []);

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New Ad Space</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
            label="User ID"
            type="number"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            fullWidth
            margin="dense"
            required
        />
        <TextField
          label="Geo Name"
          value={adSpace.geoName ?? ''}
          onChange={e => handleChange('geoName', e.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="Description"
          value={adSpace.description ?? ''}
          onChange={e => handleChange('description', e.target.value)}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
            <InputLabel id="media-type-label">Ad Space Media Type</InputLabel>
            <Select
            labelId="media-type-label"
            value={adSpace.adSpaceMediaTypeId ?? ''}
            label="Ad Space Media Type"
            onChange={e => handleChange('adSpaceMediaTypeId', Number(e.target.value))}
            required
            >
            {mediaTypes.map(mt => (
                <MenuItem key={mt.id} value={mt.id}>
                {mt.name}
                </MenuItem>
            ))}
            </Select>
        </FormControl>
        <TextField
          label="Width"
          type="number"
          value={adSpace.sizeWidth === 0 ? '' : adSpace.sizeWidth ?? ''}
          onChange={e => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            handleChange('sizeWidth', value);
          }}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Height"
          type="number"
          value={adSpace.sizeHeight === 0 ? '' : adSpace.sizeHeight ?? ''}
          onChange={e => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            handleChange('sizeHeight', value);
          }}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
            <InputLabel id="size-data-type-label">Size Data Type</InputLabel>
            <Select
            labelId="size-data-type-label"
            value={adSpace.sizeDataTypeId ?? ''}
            label="Size Data Type"
            onChange={e => handleChange('sizeDataTypeId', Number(e.target.value))}
            required
            >
            {sizeDataTypes.map(sdt => (
                <MenuItem key={sdt.id} value={sdt.id}>
                {sdt.unitName}
                </MenuItem>
            ))}
            </Select>
        </FormControl>
        <TextField
          label="Country"
          value={adSpace.country ?? ''}
          onChange={e => handleChange('country', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Province"
          value={adSpace.province ?? ''}
          onChange={e => handleChange('province', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="City"
          value={adSpace.city ?? ''}
          onChange={e => handleChange('city', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Area"
          value={adSpace.area ?? ''}
          onChange={e => handleChange('area', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Street"
          value={adSpace.street ?? ''}
          onChange={e => handleChange('street', e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
            label="Latitude"
            type="number"
            value={lat}
            onChange={e => setLat(e.target.value)}
            fullWidth
            margin="dense"
            helperText="Google Maps format: latitude, longitude → Enter as shown"
        />
        <TextField
            label="Longitude"
            type="number"
            value={lng}
            onChange={e => setLng(e.target.value)}
            fullWidth
            margin="dense"
            helperText="Stored as GeoJSON [longitude, latitude] in database"
        />
        <TextField
          label="Price Weekly"
          type="number"
          value={adSpace.priceWeekly === 0 ? '' : adSpace.priceWeekly ?? ''}
          onChange={e => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            handleChange('priceWeekly', value);
          }}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Price Monthly"
          type="number"
          value={adSpace.priceMonthly === 0 ? '' : adSpace.priceMonthly ?? ''}
          onChange={e => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            handleChange('priceMonthly', value);
          }}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Price Yearly"
          type="number"
          value={adSpace.priceYearly === 0 ? '' : adSpace.priceYearly ?? ''}
          onChange={e => {
            const value = e.target.value === '' ? 0 : Number(e.target.value);
            handleChange('priceYearly', value);
          }}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="currency-label">Currency</InputLabel>
          <Select
            labelId="currency-label"
            value={adSpace.currencyId ?? ''}
            label="Currency"
            onChange={e => handleChange('currencyId', Number(e.target.value))}
            required
          >
            {currencies.map(currency => (
              <MenuItem key={currency.id} value={currency.id}>
                {currency.code} - {currency.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Available From"
          type="datetime-local"
          value={
            adSpace.availableFrom
              ? new Date(adSpace.availableFrom).toISOString().slice(0, 16)
              : ''
          }
          onChange={e => handleChange('availableFrom', new Date(e.target.value))}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={adSpace.active !== undefined ? (adSpace.active ? 'yes' : 'no') : 'yes'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create Ad Space
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdSpacesAdminAddAdSpacePage;